import { api } from "./client";
import type { UserInfo } from "@/store/auth";

// ---------- Common types ----------
export interface PaginatedList<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// ---------- Auth ----------
export interface LoginRequest { email: string; password: string; branchId?: string | null }
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: UserInfo;
}

export const authApi = {
  login: (req: LoginRequest) =>
    api.post<LoginResponse>("/api/v1/auth/login", req).then(r => r.data),
  logout: () => api.post("/api/v1/auth/logout"),
};

// ---------- Items ----------
export interface ItemDto {
  id: string;
  sku: string;
  name: string;
  genericName?: string;
  itemType: string;
  hsnCode?: string;
  baseUomId: string;
  costingMethod: string;
  reorderLevel: number;
  reorderQty: number;
  minOrderQty?: number;
  safetyStock: number;
  leadTimeDays: number;
  isBatchTracked: boolean;
  isExpiryTracked: boolean;
  form?: string;
  packaging?: string;
  strength?: string;
  manufacturer?: string;
  category?: string;
  defaultMrp?: number;
  gstRate: number;
  isScheduleDrug: boolean;
  scheduleCategory?: string;
}

export interface SubstituteOptionDto {
  itemId: string; sku: string; name: string; genericName?: string;
  form?: string; strength?: string; manufacturer?: string;
  priority: number;
  reasonCode: string;
  requiresPharmacistConfirm: boolean;
  availableQty: number; mrp?: number;
}

export const itemsApi = {
  list: (params: { search?: string; itemType?: string; category?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<ItemDto>>("/api/v1/items", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<ItemDto>(`/api/v1/items/${id}`).then(r => r.data),
  create: (payload: Partial<ItemDto>) =>
    api.post<{ id: string }>("/api/v1/items", payload).then(r => r.data),
  update: (id: string, payload: Partial<ItemDto>) =>
    api.put(`/api/v1/items/${id}`, { id, ...payload }),
  delete: (id: string) =>
    api.delete(`/api/v1/items/${id}`),
  substitutes: (itemId: string, warehouseId: string) =>
    api.get<SubstituteOptionDto[]>(`/api/v1/items/${itemId}/substitutes`, { params: { warehouseId } }).then(r => r.data),
};

// ---------- Vendors / Customers ----------
export interface VendorDto {
  id: string; code: string; name: string;
  gstin?: string; pan?: string; contactPerson?: string;
  phone?: string; email?: string;
  paymentTerms?: string; creditDays?: number; isActive: boolean;
}

export interface CustomerDto {
  id: string; code: string; name: string;
  phone?: string; email?: string; uhid?: string;
  creditLimit?: number; isActive: boolean;
  /** India GSTIN (GST regime). */
  gstin?: string | null;
  /** Regime-neutral tax registration (VAT regime — UAE TRN, KSA TRN, UK VAT). */
  taxRegistrationNumber?: string | null;
}

export const vendorsApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<VendorDto>>("/api/v1/vendors", { params }).then(r => r.data),
  get: (id: string) => api.get<VendorDto>(`/api/v1/vendors/${id}`).then(r => r.data),
  create: (payload: Partial<VendorDto>) =>
    api.post<{ id: string }>("/api/v1/vendors", payload).then(r => r.data),
  update: (id: string, payload: Partial<VendorDto>) =>
    api.put(`/api/v1/vendors/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/vendors/${id}`),
};

export const customersApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<CustomerDto>>("/api/v1/customers", { params }).then(r => r.data),
  get: (id: string) => api.get<CustomerDto>(`/api/v1/customers/${id}`).then(r => r.data),
  create: (payload: Partial<CustomerDto>) =>
    api.post<{ id: string }>("/api/v1/customers", payload).then(r => r.data),
  update: (id: string, payload: Partial<CustomerDto>) =>
    api.put(`/api/v1/customers/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/customers/${id}`),
};

// ---------- Stock ----------
export interface StockAvailabilityDto {
  itemId: string; itemName: string; sku: string;
  batchId: string; batchNo: string; expiryDate?: string;
  warehouseId: string; warehouseName: string;
  onHandQty: number; reservedQty: number; availableQty: number;
  costPrice: number; mrp: number;
}

export const stockApi = {
  availability: (params: { itemId?: string; warehouseId?: string } = {}) =>
    api.get<StockAvailabilityDto[]>("/api/v1/stock/availability", { params }).then(r => r.data),
};

// ---------- Purchase Orders ----------
export interface PurchaseOrderListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  vendorId: string; vendorName: string; warehouseId: string;
  expectedDeliveryDate?: string; totalAmount: number; lineCount: number;
}

export interface CreatePoLineRequest {
  itemId: string; qty: number; rate: number; taxAmount: number;
}

export interface CreatePurchaseOrderRequest {
  vendorId: string;
  warehouseId: string;
  documentDate: string;
  expectedDeliveryDate?: string;
  vendorRef?: string;
  notes?: string;
  lines: CreatePoLineRequest[];
}

export const purchaseOrdersApi = {
  list: (params: { status?: string; vendorId?: string; fromDate?: string; toDate?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PurchaseOrderListDto>>("/api/v1/purchase-orders", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<PurchaseOrderDetailDto>(`/api/v1/purchase-orders/${id}`).then(r => r.data),
  create: (payload: CreatePurchaseOrderRequest) =>
    api.post<{ id: string }>("/api/v1/purchase-orders", payload).then(r => r.data),
};

// ---------- GRN ----------
export interface CreateGrnLineRequest {
  purchaseOrderLineId: string; itemId: string;
  batchNo: string; expiryDate?: string;
  receivedQty: number; acceptedQty: number; rejectedQty: number;
  unitCost: number; remarks?: string;
}

export interface CreateGrnRequest {
  purchaseOrderId: string; warehouseId: string;
  documentDate: string; deliveryNoteRef?: string; notes?: string;
  lines: CreateGrnLineRequest[];
}

export const grnApi = {
  list: (params: { status?: string; vendorId?: string; purchaseOrderId?: string; page?: number; pageSize?: number } = {}) =>
    api.get(`/api/v1/grns`, { params }).then(r => r.data),
  get: (id: string) => api.get(`/api/v1/grns/${id}`).then(r => r.data),
  create: (payload: CreateGrnRequest) =>
    api.post<{ id: string }>("/api/v1/grns", payload).then(r => r.data),
  post: (id: string) => api.post(`/api/v1/grns/${id}/post`),
};

// ---------- Accounting + Reports + Audit (S5) ----------
export interface AccountDto {
  id: string; accountGroupId?: string; accountGroupName?: string;
  code: string; name: string; accountType: string;
  isActive: boolean; displayOrder?: number;
}
export const accountsApi = {
  list: (params: { search?: string; type?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<AccountDto>>("/api/v1/accounts", { params }).then(r => r.data),
};

export interface JournalLineInput {
  accountId: string; debit: number; credit: number; description?: string;
}
export interface CreateJournalEntryRequest {
  entryDate: string; description?: string;
  referenceType?: string; referenceId?: string;
  lines: JournalLineInput[];
}
export const journalsWriteApi = {
  create: (payload: CreateJournalEntryRequest) =>
    api.post<{ id: string }>("/api/v1/journal-entries", payload).then(r => r.data),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/journal-entries/${id}/post`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

// ---------- Journals (read) ----------
export interface JournalLineReadDto {
  id: string; accountId: string; accountCode: string; accountName: string;
  debit: number; credit: number; description?: string;
}
export interface JournalEntryListDto {
  id: string; entryNo: string; entryDate: string; status: string;
  referenceType?: string; referenceId?: string; description?: string;
  totalDebit: number; totalCredit: number; isAutoGenerated: boolean;
}
export interface JournalEntryDetailDto extends JournalEntryListDto {
  rowVersion: string;
  lines: JournalLineReadDto[];
}
export const journalsApi = {
  list: (params: {
    status?: string; referenceType?: string;
    fromDate?: string; toDate?: string;
    page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<JournalEntryListDto>>("/api/v1/journal-entries", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<JournalEntryDetailDto>(`/api/v1/journal-entries/${id}`).then(r => r.data),
};

export interface PeriodDto {
  id: string; financialYearId: string; name: string;
  startDate: string; endDate: string; isClosed: boolean;
  rowVersion: string;
}
export interface CreatePeriodRequest {
  financialYearId: string; name: string;
  startDate: string; endDate: string;
}
// ---------- Roles write (S6.1) ----------
export interface PermissionDto {
  id: string; code: string; module: string; resource: string; action: string;
  description?: string;
}
export interface RoleWithPermissionsDto {
  id: string; name: string; code: string; isSystem: boolean;
  permissions: PermissionDto[];
}
export const permissionsApi = {
  list: (module?: string) =>
    api.get<PermissionDto[]>("/api/v1/permissions", { params: { module } }).then(r => r.data),
};
export const rolesApi = {
  list: () => api.get<{ id: string; name: string; code: string; isSystem: boolean; permissionCount: number }[]>("/api/v1/roles").then(r => r.data),
  get: (id: string) => api.get<RoleWithPermissionsDto>(`/api/v1/roles/${id}`).then(r => r.data),
  create: (payload: { name: string; code: string }) =>
    api.post<{ id: string }>("/api/v1/roles", payload).then(r => r.data),
  assignPermissions: (id: string, permissionCodes: string[]) =>
    api.put(`/api/v1/roles/${id}/permissions`, { permissionCodes }),
};

// ---------- Branches write (S6.2) ----------
export interface BranchDetailDto {
  id: string; code: string; name: string;
  gstin?: string; addressJson?: string;
  isActive: boolean; rowVersion: string;
}
export const branchesWriteApi = {
  get: (id: string) => api.get<BranchDetailDto>(`/api/v1/branches/${id}`).then(r => r.data),
  create: (payload: { code: string; name: string; gstin?: string; addressJson?: string }) =>
    api.post<{ id: string }>("/api/v1/branches", payload).then(r => r.data),
  update: (id: string, payload: { id: string; code: string; name: string; gstin?: string; addressJson?: string; isActive: boolean }) =>
    api.put(`/api/v1/branches/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/branches/${id}`),
};

// ---------- Warehouses (S6.3) ----------
export type WarehouseType = "Main" | "Satellite" | "Cold" | "Quarantine" | "Consignment";
export interface WarehouseDto {
  id: string; code: string; name: string;
  warehouseType: WarehouseType;
  branchId: string; branchName: string;
  managerUserId?: string; isActive: boolean;
}
export interface WarehouseDetailDto extends WarehouseDto { rowVersion: string }
export const warehousesApi = {
  list: (params: { branchId?: string; type?: WarehouseType; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<WarehouseDto>>("/api/v1/warehouses", { params }).then(r => r.data),
  get: (id: string) => api.get<WarehouseDetailDto>(`/api/v1/warehouses/${id}`).then(r => r.data),
  create: (payload: { code: string; name: string; warehouseType: WarehouseType; branchId: string; managerUserId?: string }) =>
    api.post<{ id: string }>("/api/v1/warehouses", payload).then(r => r.data),
  update: (id: string, payload: { id: string; code: string; name: string; warehouseType: WarehouseType; managerUserId?: string; isActive: boolean }) =>
    api.put(`/api/v1/warehouses/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/warehouses/${id}`),
};

// ---------- Feature flags ----------
export type FeaturePhaseName = "Mvp1" | "Mvp2" | "Final";
export interface FeatureFlagDto {
  featureKey: string;
  phase: FeaturePhaseName;
  description: string;
  defaultEnabled: boolean;
  isEnabled: boolean;
}
export const featureFlagsApi = {
  list: () => api.get<FeatureFlagDto[]>("/api/v1/feature-flags").then(r => r.data),
  set: (featureKey: string, enabled: boolean) =>
    api.put(`/api/v1/feature-flags/${encodeURIComponent(featureKey)}`, { enabled }),
};

// ---------- Tenant settings (Phase 5 — read-only) ----------
export interface TenantSettingsDto {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
  taxRegime: "None" | "Gst" | "Vat";
  createdAt: string;
}

export const tenantsApi = {
  current: () => api.get<TenantSettingsDto>("/api/v1/tenants/current").then(r => r.data),
};

// ---------- Quotations (UC-M1-PROC-07) ----------
export type DocumentStatusName = "Draft" | "Submitted" | "Approved" | "Posted" | "Cancelled";
export interface QuotationLineReadDto {
  id: string; itemId: string; itemSku: string; itemName: string;
  qty: number; requestedRate: number; quotedRate?: number; taxAmount: number;
  effectiveRate: number; lineTotal: number;
}
export interface QuotationListDto {
  id: string; documentNo: string; documentDate: string; status: DocumentStatusName;
  vendorId: string; vendorName: string; validUntilDate?: string;
  totalAmount: number; convertedToPurchaseOrderId?: string;
}
export interface QuotationDetailDto extends Omit<QuotationListDto, "vendorName"> {
  vendorName: string; warehouseId: string; vendorRef?: string; notes?: string;
  lines: QuotationLineReadDto[]; rowVersion: string;
}
export interface QuotationLineInput {
  itemId: string; qty: number; requestedRate: number; taxAmount: number;
}
export const quotationsApi = {
  list: (params: { status?: DocumentStatusName; vendorId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<QuotationListDto>>("/api/v1/quotations", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<QuotationDetailDto>(`/api/v1/quotations/${id}`).then(r => r.data),
  create: (payload: {
    vendorId: string; warehouseId: string; validUntilDate?: string;
    vendorRef?: string; notes?: string; lines: QuotationLineInput[];
  }) => api.post<{ id: string }>("/api/v1/quotations", payload).then(r => r.data),
  submit: (id: string, rowVersion: string) =>
    api.post(`/api/v1/quotations/${id}/submit`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  approve: (id: string, rowVersion: string,
            lines: { lineId: string; quotedRate: number }[]) =>
    api.post(`/api/v1/quotations/${id}/approve`, { id, lines }, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  cancel: (id: string, rowVersion: string) =>
    api.post(`/api/v1/quotations/${id}/cancel`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  convertToPo: (id: string, rowVersion: string) =>
    api.post<{ purchaseOrderId: string }>(`/api/v1/quotations/${id}/convert-to-po`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }).then(r => r.data),
  delete: (id: string) => api.delete(`/api/v1/quotations/${id}`),
};

// ---------- Billing offers (UC-M1-SAL-05) ----------
export type DiscountType = "Percent" | "Amount";
export interface BillingOfferDto {
  id: string; name: string; description?: string;
  itemId?: string; itemName?: string;
  discountType: DiscountType; discountValue: number;
  validFrom: string; validTo: string; isActive: boolean;
}
export const billingOffersApi = {
  active: (itemId?: string) =>
    api.get<BillingOfferDto[]>("/api/v1/billing-offers/active", {
      params: itemId ? { itemId } : {},
    }).then(r => r.data),
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<BillingOfferDto>>("/api/v1/billing-offers", { params }).then(r => r.data),
  create: (payload: {
    name: string; description?: string; itemId?: string;
    discountType: DiscountType; discountValue: number;
    validFrom: string; validTo: string;
  }) => api.post<{ id: string }>("/api/v1/billing-offers", payload).then(r => r.data),
  update: (id: string, payload: {
    id: string; name: string; description?: string; itemId?: string;
    discountType: DiscountType; discountValue: number;
    validFrom: string; validTo: string; isActive: boolean;
  }) => api.put(`/api/v1/billing-offers/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/billing-offers/${id}`),
};

export const periodsApi = {
  list: (params: { page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PeriodDto>>("/api/v1/financial-periods", { params }).then(r => r.data),
  create: (payload: CreatePeriodRequest) =>
    api.post<{ id: string }>("/api/v1/financial-periods", payload).then(r => r.data),
  close: (id: string, rowVersion: string) =>
    api.post(`/api/v1/financial-periods/${id}/close`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  reopen: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/financial-periods/${id}/reopen`, { reason }, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

export interface TrialBalanceRow {
  accountId: string; code: string; name: string; accountType: string;
  totalDebit: number; totalCredit: number; net: number;
}
export interface TrialBalanceReport {
  asOf: string; totalDebit: number; totalCredit: number; isBalanced: boolean;
  rows: TrialBalanceRow[];
}
export interface StockOnHandRow {
  itemId: string; sku: string; itemName: string; form?: string; strength?: string;
  batchId: string; batchNo?: string; expiryDate?: string;
  warehouseId: string; warehouseName: string;
  onHand: number; reserved: number; available: number;
}
export interface PoStatusRow {
  vendorId: string; vendorName: string;
  draft: number; submitted: number; approved: number; posted: number; cancelled: number;
  totalValue: number;
}
export interface DailySalesRow {
  day: string; billCount: number; subTotal: number; tax: number; netAmount: number;
}
export const reportsApi = {
  trialBalance: (asOf?: string) =>
    api.get<TrialBalanceReport>("/api/v1/reports/trial-balance", { params: { asOf } }).then(r => r.data),
  stockOnHand: (params: { warehouseId?: string; itemId?: string } = {}) =>
    api.get<StockOnHandRow[]>("/api/v1/reports/stock-on-hand", { params }).then(r => r.data),
  poStatus: (params: { fromDate?: string; toDate?: string } = {}) =>
    api.get<PoStatusRow[]>("/api/v1/reports/po-status", { params }).then(r => r.data),
  dailySales: (fromDate: string, toDate: string) =>
    api.get<DailySalesRow[]>("/api/v1/reports/daily-sales", { params: { fromDate, toDate } }).then(r => r.data),
};

export interface AuditLogDto {
  id: string; tenantId: string; branchId?: string; userId?: string;
  action: string; entityType?: string; entityId?: string;
  beforeJson?: unknown; afterJson?: unknown;
  ipAddress?: string; correlationId?: string;
  occurredAt: string;
}
export const auditApi = {
  list: (params: {
    userId?: string; action?: string; entityType?: string; entityId?: string;
    fromDate?: string; toDate?: string; page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<AuditLogDto>>("/api/v1/audit-logs", { params }).then(r => r.data),
};

// ---------- Pricing + Requisitions + HealNTrack + Reports (S9) ----------
export type PriceListType = "OP" | "IP" | "Corporate" | "Insurance" | "CustomerSpecific";

export interface PriceListDto {
  id: string; code: string; name: string; listType: PriceListType;
  customerId?: string; isActive: boolean; priority: number; lineCount: number;
}
export interface PriceListLineDto {
  id: string; itemId: string; itemSku: string; itemName: string;
  rate: number; effectiveFrom: string; effectiveTo?: string;
}
export interface PriceListDetailDto extends PriceListDto {
  rowVersion: string; lines: PriceListLineDto[];
}
export interface UpsertPriceListLineRequest {
  id?: string; itemId: string; rate: number;
  effectiveFrom: string; effectiveTo?: string;
}
export const priceListsApi = {
  list: (params: { type?: PriceListType; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PriceListDto>>("/api/v1/price-lists", { params }).then(r => r.data),
  get: (id: string) => api.get<PriceListDetailDto>(`/api/v1/price-lists/${id}`).then(r => r.data),
  create: (payload: { code: string; name: string; listType: PriceListType; customerId?: string; priority: number; lines: UpsertPriceListLineRequest[] }) =>
    api.post<{ id: string }>("/api/v1/price-lists", payload).then(r => r.data),
  updateLines: (id: string, lines: UpsertPriceListLineRequest[]) =>
    api.put(`/api/v1/price-lists/${id}/lines`, { lines }),
  delete: (id: string) => api.delete(`/api/v1/price-lists/${id}`),
};

export const pricingApi = {
  resolve: (params: { itemId: string; customerId?: string; context?: PriceListType; onDate?: string }) =>
    api.get<{ itemId: string; rate: number; priceListId?: string; source: string }>(
      "/api/v1/pricing/resolve", { params }).then(r => r.data),
};

export interface PurchaseRequisitionListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  requestingBranchId: string; neededByDate?: string;
  lineCount: number; convertedToPurchaseOrderId?: string;
}
export interface PurchaseRequisitionLineReadDto {
  id: string; itemId: string; itemName: string; sku: string;
  requestedQty: number; reason?: string;
}
export interface PurchaseRequisitionDetailDto extends PurchaseRequisitionListDto {
  departmentRef?: string; reason?: string; rowVersion: string;
  lines: PurchaseRequisitionLineReadDto[];
}
export const purchaseRequisitionsApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PurchaseRequisitionListDto>>("/api/v1/purchase-requisitions", { params }).then(r => r.data),
  get: (id: string) => api.get<PurchaseRequisitionDetailDto>(`/api/v1/purchase-requisitions/${id}`).then(r => r.data),
  create: (payload: { departmentRef?: string; neededByDate?: string; reason?: string; lines: { itemId: string; qty: number; reason?: string }[] }) =>
    api.post<{ id: string }>("/api/v1/purchase-requisitions", payload).then(r => r.data),
  submit: (id: string, rowVersion: string) =>
    api.post(`/api/v1/purchase-requisitions/${id}/submit`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  approve: (id: string, rowVersion: string) =>
    api.post(`/api/v1/purchase-requisitions/${id}/approve`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  reject: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/purchase-requisitions/${id}/reject`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
  convertToPo: (id: string, payload: { vendorId: string; warehouseId: string; expectedDeliveryDate?: string; rateByLine: Record<string, number> }) =>
    api.post<{ purchaseOrderId: string }>(`/api/v1/purchase-requisitions/${id}/convert`, payload).then(r => r.data),
};

export interface HealNTrackPatientDto {
  uhid: string; fullName: string; gender: string; dob: string;
  phone?: string; branchCode?: string; admissionId?: string; wardCode?: string; isAdmitted: boolean;
}
export const healNTrackApi = {
  patient: (uhid: string) =>
    api.get<HealNTrackPatientDto>(`/api/v1/integrations/healntrack/v1/patients/${encodeURIComponent(uhid)}`).then(r => r.data),
};

export interface ConsolidatedStockRow {
  itemId: string; sku: string; itemName: string; form?: string; strength?: string;
  totalOnHand: number; totalReserved: number; totalAvailable: number; warehouseCount: number;
}
export interface AgingBucket { bucket0_30: number; bucket31_60: number; bucket61_90: number; bucketOver90: number }
export interface ArAgingRow { customerId: string; customerName: string; buckets: AgingBucket; total: number }
export interface ApAgingRow { vendorId: string; vendorName: string; buckets: AgingBucket; total: number }
export const reportsApiV2 = {
  consolidatedStock: () =>
    api.get<ConsolidatedStockRow[]>("/api/v1/reports/consolidated-stock").then(r => r.data),
  arAging: () => api.get<ArAgingRow[]>("/api/v1/reports/ar-aging").then(r => r.data),
  apAging: () => api.get<ApAgingRow[]>("/api/v1/reports/ap-aging").then(r => r.data),
};

// ---------- Shipments + Carriers + Vehicles + Drivers (S8) ----------
export type ShipmentStatus =
  | "Planned" | "Loaded" | "Dispatched" | "InTransit" | "Arrived" | "Delivered" | "Closed" | "Exception";

export interface ShipmentListDto {
  id: string; transferOrderId: string; transferOrderNo: string;
  carrierId?: string; carrierName?: string;
  vehicleId?: string; vehicleRegNo?: string;
  driverId?: string; driverName?: string;
  status: ShipmentStatus;
  requestedDeliveryDate?: string; actualDeliveryDate?: string;
  podStatus?: string;
  lineCount: number; milestoneCount: number;
}
export interface ShipmentMilestoneDto { id: string; status: ShipmentStatus; occurredAt: string; notes?: string }
export interface ProofOfDeliveryDto {
  id: string; receiverName: string;
  signatureUrl?: string; photoUrl?: string; notes?: string; capturedAt: string;
}
export interface ShipmentDetailDto extends ShipmentListDto {
  vehicleNo?: string; eWayBillNo?: string;
  rowVersion: string;
  lines: { id: string; transferLineId: string; qty: number }[];
  milestones: ShipmentMilestoneDto[];
  pod?: ProofOfDeliveryDto;
}
export const shipmentsApi = {
  list: (params: { status?: ShipmentStatus; transferOrderId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<ShipmentListDto>>("/api/v1/shipments", { params }).then(r => r.data),
  get: (id: string) => api.get<ShipmentDetailDto>(`/api/v1/shipments/${id}`).then(r => r.data),
  create: (payload: { transferOrderId: string; carrierId?: string; vehicleId?: string; driverId?: string; eWayBillNo?: string; requestedDeliveryDate?: string }) =>
    api.post<{ id: string }>("/api/v1/shipments", payload).then(r => r.data),
  addMilestone: (id: string, status: ShipmentStatus, notes: string | undefined, rowVersion: string) =>
    api.post(`/api/v1/shipments/${id}/milestones`, { status, notes }, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  capturePod: (id: string, body: { receiverName: string; signatureUrl?: string; photoUrl?: string; notes?: string }, rowVersion: string) =>
    api.post(`/api/v1/shipments/${id}/pod`, body, { headers: { "If-Match": `"${rowVersion}"` } }),
};

export interface CarrierDto { id: string; code: string; name: string; phone?: string; isActive: boolean }
export const carriersApi = {
  list: (params: { search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<CarrierDto>>("/api/v1/carriers", { params }).then(r => r.data),
  create: (payload: { code: string; name: string; phone?: string }) =>
    api.post<{ id: string }>("/api/v1/carriers", payload).then(r => r.data),
  update: (id: string, payload: { id: string; code: string; name: string; phone?: string; isActive: boolean }) =>
    api.put(`/api/v1/carriers/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/carriers/${id}`),
};

export interface VehicleDto {
  id: string; registrationNo: string;
  carrierId?: string; carrierName?: string;
  capacityKg: number; coldChainCapable: boolean;
  insuranceExpiry?: string; isActive: boolean;
}
export const vehiclesApi = {
  list: (params: { search?: string; carrierId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<VehicleDto>>("/api/v1/vehicles", { params }).then(r => r.data),
  create: (payload: { registrationNo: string; carrierId?: string; capacityKg: number; coldChainCapable: boolean; insuranceExpiry?: string }) =>
    api.post<{ id: string }>("/api/v1/vehicles", payload).then(r => r.data),
  update: (id: string, payload: { id: string; registrationNo: string; carrierId?: string; capacityKg: number; coldChainCapable: boolean; insuranceExpiry?: string; isActive: boolean }) =>
    api.put(`/api/v1/vehicles/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/vehicles/${id}`),
};

export interface DriverDto {
  id: string; name: string;
  licenseNo?: string; licenseExpiry?: string;
  mobile?: string; carrierId?: string; carrierName?: string; isActive: boolean;
}
export const driversApi = {
  list: (params: { search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<DriverDto>>("/api/v1/drivers", { params }).then(r => r.data),
  create: (payload: { name: string; licenseNo?: string; licenseExpiry?: string; mobile?: string; carrierId?: string }) =>
    api.post<{ id: string }>("/api/v1/drivers", payload).then(r => r.data),
  update: (id: string, payload: { id: string; name: string; licenseNo?: string; licenseExpiry?: string; mobile?: string; carrierId?: string; isActive: boolean }) =>
    api.put(`/api/v1/drivers/${id}`, payload),
  delete: (id: string) => api.delete(`/api/v1/drivers/${id}`),
};

// ---------- Transfer Orders (S7.1) ----------
export interface TransferOrderListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  sourceWarehouseId: string; sourceWarehouseName: string;
  destWarehouseId: string; destWarehouseName: string;
  lineCount: number;
  totalRequested: number; totalDispatched: number; totalReceived: number;
  inTransitQty: number;
  transferRequisitionId?: string;
}
export interface TransferOrderLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string; expiryDate?: string;
  requestedQty: number; dispatchedQty: number; receivedQty: number;
  inTransitQty: number; pendingDispatch: number; pendingReceipt: number;
  requisitionLineId?: string;
}
export interface TransferOrderDetailDto extends TransferOrderListDto {
  expectedDeliveryDate?: string; notes?: string;
  rowVersion: string; lines: TransferOrderLineDto[];
}
export interface CreateTransferOrderLineRequest {
  itemId: string; batchId: string; qty: number; requisitionLineId?: string;
}
export interface CreateTransferOrderRequest {
  sourceWarehouseId: string; destWarehouseId: string;
  expectedDeliveryDate?: string; notes?: string;
  transferRequisitionId?: string;
  lines: CreateTransferOrderLineRequest[];
}
export const transferOrdersApi = {
  list: (params: { status?: string; sourceWarehouseId?: string; destWarehouseId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<TransferOrderListDto>>("/api/v1/transfer-orders", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<TransferOrderDetailDto>(`/api/v1/transfer-orders/${id}`).then(r => r.data),
  create: (payload: CreateTransferOrderRequest) =>
    api.post<{ id: string }>("/api/v1/transfer-orders", payload).then(r => r.data),
  submit: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/submit`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  approve: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/approve`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  reject: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/reject`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
  cancel: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/cancel`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
  dispatch: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/dispatch`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  receive: (id: string, lines: { lineId: string; receivedQty: number }[], rowVersion: string) =>
    api.post(`/api/v1/transfer-orders/${id}/receive`, { lines }, { headers: { "If-Match": `"${rowVersion}"` } }),
};

// ---------- Transfer Requisitions (S7.2) ----------
export interface TransferRequisitionListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  requestingBranchId: string; requestingWarehouseId: string; requestingWarehouseName: string;
  suggestedSourceWarehouseId?: string; suggestedSourceWarehouseName?: string;
  neededByDate?: string; lineCount: number;
  convertedToTransferOrderId?: string;
}
export interface TransferRequisitionLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  requestedQty: number; reason?: string;
}
export interface TransferRequisitionDetailDto extends TransferRequisitionListDto {
  reason?: string; rowVersion: string; lines: TransferRequisitionLineDto[];
}
export interface CreateTransferRequisitionLineRequest { itemId: string; qty: number; reason?: string }
export interface CreateTransferRequisitionRequest {
  requestingWarehouseId: string; suggestedSourceWarehouseId?: string;
  neededByDate?: string; reason?: string;
  lines: CreateTransferRequisitionLineRequest[];
}
export const transferRequisitionsApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<TransferRequisitionListDto>>("/api/v1/transfer-requisitions", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<TransferRequisitionDetailDto>(`/api/v1/transfer-requisitions/${id}`).then(r => r.data),
  create: (payload: CreateTransferRequisitionRequest) =>
    api.post<{ id: string }>("/api/v1/transfer-requisitions", payload).then(r => r.data),
  submit: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-requisitions/${id}/submit`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  approve: (id: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-requisitions/${id}/approve`, null, { headers: { "If-Match": `"${rowVersion}"` } }),
  reject: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/transfer-requisitions/${id}/reject`, { reason }, { headers: { "If-Match": `"${rowVersion}"` } }),
  convert: (id: string, sourceWarehouseId: string, batchByLine: Record<string, string>) =>
    api.post<{ transferOrderId: string }>(`/api/v1/transfer-requisitions/${id}/convert`,
      { sourceWarehouseId, batchByLine }).then(r => r.data),
};

// ---------- Purchase Invoices ----------
export type PurchaseInvoiceSource = "FromGrn" | "Direct";
export interface PurchaseInvoiceListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  source: PurchaseInvoiceSource;
  vendorId: string; vendorName: string;
  grnId?: string | null; grnNo?: string | null;
  vendorInvoiceNo?: string; vendorInvoiceDate?: string;
  subTotal: number; taxAmount: number; discountAmount: number; totalAmount: number;
}
export interface PurchaseInvoiceLineDto {
  id: string; grnLineId?: string | null; itemId: string; itemName: string; sku: string;
  qty: number; rate: number; taxAmount: number; discountAmount: number; lineTotal: number;
  batchNo?: string | null; expiryDate?: string | null; batchId?: string | null;
}
export interface PurchaseInvoiceDetailDto extends PurchaseInvoiceListDto {
  journalEntryId?: string; notes?: string; lines: PurchaseInvoiceLineDto[];
}
export interface CreatePurchaseInvoiceLineRequest {
  grnLineId: string; itemId: string; qty: number; rate: number;
  taxAmount: number; discountAmount: number;
}
export interface CreatePurchaseInvoiceRequest {
  grnId: string; documentDate: string;
  vendorInvoiceNo?: string; vendorInvoiceDate?: string;
  discountAmount: number; notes?: string;
  lines: CreatePurchaseInvoiceLineRequest[];
}
export interface CreateDirectPurchaseInvoiceLineRequest {
  itemId: string; batchNo: string; expiryDate?: string;
  qty: number; rate: number; taxAmount: number; discountAmount: number;
}
export interface CreateDirectPurchaseInvoiceRequest {
  vendorId: string; warehouseId: string; documentDate: string;
  vendorInvoiceNo?: string; vendorInvoiceDate?: string;
  discountAmount: number; notes?: string;
  lines: CreateDirectPurchaseInvoiceLineRequest[];
}
export const purchaseInvoicesApi = {
  list: (params: { status?: string; vendorId?: string; fromDate?: string; toDate?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<PurchaseInvoiceListDto>>("/api/v1/purchase-invoices", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<PurchaseInvoiceDetailDto>(`/api/v1/purchase-invoices/${id}`).then(r => r.data),
  create: (payload: CreatePurchaseInvoiceRequest) =>
    api.post<{ id: string }>("/api/v1/purchase-invoices", payload).then(r => r.data),
  createDirect: (payload: CreateDirectPurchaseInvoiceRequest) =>
    api.post<{ id: string }>("/api/v1/purchase-invoices/direct", payload).then(r => r.data),
  post: (id: string) => api.post(`/api/v1/purchase-invoices/${id}/post`),
};

// ---------- Sales Invoices ----------
export interface SalesInvoiceListDto {
  id: string; documentNo: string; documentDate: string; status: string; billType: string;
  customerId?: string; customerName?: string; customerPhone?: string;
  netAmount: number; totalReceived: number; balanceDue: number; lineCount: number;
}

export type LineType = "Rx" | "Item" | "Service" | "Other";

export interface CreateSalesInvoiceLineRequest {
  lineType: LineType;
  itemId?: string;
  serviceId?: string;
  batchId?: string;
  description?: string;
  prescriptionRef?: string;
  substitutedFromItemId?: string;
  substituteReasonCode?: string;
  qty: number;
  rate: number;
  taxAmount: number;
  discountAmount: number;
  lineNo: number;
}

export interface CreateSalesInvoiceRequest {
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  billType: "OutPatient" | "InPatient";
  warehouseId: string;
  documentDate: string;
  discountAmount: number;
  notes?: string;
  lines: CreateSalesInvoiceLineRequest[];
}

export interface PaymentLineRequest {
  mode: "Cash" | "Card" | "Upi" | "BankTransfer" | "Cheque" | "Credit";
  amountReceived: number;
  reference?: string;
}

export const salesInvoicesApi = {
  list: (params: { status?: string; billType?: string; customerId?: string; fromDate?: string; toDate?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<SalesInvoiceListDto>>("/api/v1/sales-invoices", { params }).then(r => r.data),
  get: (id: string) =>
    api.get(`/api/v1/sales-invoices/${id}`).then(r => r.data),
  create: (payload: CreateSalesInvoiceRequest) =>
    api.post<{ id: string }>("/api/v1/sales-invoices", payload).then(r => r.data),
  submit: (id: string, rowVersion: string) =>
    api.post(`/api/v1/sales-invoices/${id}/submit`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  post: (id: string, payments: PaymentLineRequest[]) =>
    api.post(`/api/v1/sales-invoices/${id}/post`, { payments }),
  cancel: (id: string, reason: string, rowVersion: string) =>
    api.post(`/api/v1/sales-invoices/${id}/cancel`, { reason }, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

// ---------- Item picker (S2.1, USE_CASE_CATALOG Part A1) ----------
export type PickerStockPolicy = "Issue" | "Procure" | "Source" | "Any";

export interface ItemPickerRow {
  id: string; sku: string; name: string; genericName?: string;
  form?: string; packaging?: string; strength?: string; manufacturer?: string;
  category?: string; barcode?: string;
  isScheduleDrug: boolean; isOnFormulary: boolean;
  availableQty: number; suggestedQty?: number; mrp?: number;
  isOutOfStock: boolean; isLowStock: boolean;
}

export const itemPickerApi = {
  picker: (params: {
    stockPolicy: PickerStockPolicy; warehouseId: string;
    q?: string; category?: string; inStockOnly?: boolean; formularyOnly?: boolean;
    page?: number; pageSize?: number;
  }) => api.get<ItemPickerRow[]>("/api/v1/items/picker", { params }).then(r => r.data),
  byBarcode: (code: string) =>
    api.get<ItemPickerRow>(`/api/v1/items/by-barcode/${encodeURIComponent(code)}`).then(r => r.data),
};

// ---------- Users & Roles ----------
export interface UserBranchAccessDto {
  id: string; branchId: string; branchName: string;
  roleId: string; roleName: string; warehouseIds?: string[];
}

export interface UserListDto {
  id: string; email: string; fullName: string;
  isActive: boolean; isLockedOut: boolean; mfaEnabled: boolean;
  createdAt: string; branchAccessCount: number;
}

export interface UserDetailDto extends UserListDto {
  passwordExpiresAt?: string;
  branchAccess: UserBranchAccessDto[];
}

export interface RoleDto {
  id: string; name: string; code: string; isSystem: boolean; permissionCount: number;
}

export interface BranchDto {
  id: string; code: string; name: string; isActive: boolean;
}

export interface CreateUserBranchAccessRequest {
  branchId: string; roleId: string; warehouseIds?: string[];
}

export interface CreateUserRequest {
  email: string; fullName: string; password: string; mfaEnabled: boolean;
  branchAccess: CreateUserBranchAccessRequest[];
}

export interface UpdateUserRequest {
  id: string; fullName: string; isActive: boolean; mfaEnabled: boolean;
  branchAccess: CreateUserBranchAccessRequest[];
}

export const usersApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<UserListDto>>("/api/v1/users", { params }).then(r => r.data),
  get: (id: string) => api.get<UserDetailDto>(`/api/v1/users/${id}`).then(r => r.data),
  create: (payload: CreateUserRequest) =>
    api.post<{ id: string }>("/api/v1/users", payload).then(r => r.data),
  update: (id: string, payload: UpdateUserRequest) =>
    api.put(`/api/v1/users/${id}`, payload),
  resetPassword: (id: string, newPassword: string) =>
    api.post(`/api/v1/users/${id}/reset-password`, { newPassword }),
  unlock: (id: string) => api.post(`/api/v1/users/${id}/unlock`),
};

// rolesApi (write + matrix) is exported above near the S6 admin block.

export const branchesApi = {
  list: () => api.get<BranchDto[]>("/api/v1/branches").then(r => r.data),
};

// ---------- UoM ----------
export interface UomDto { id: string; code: string; name: string }

export const uomsApi = {
  list: (params: { search?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<UomDto>>("/api/v1/uoms", { params }).then(r => r.data),
  create: (payload: { code: string; name: string }) =>
    api.post<{ id: string }>("/api/v1/uoms", payload).then(r => r.data),
  update: (id: string, payload: { name: string }) =>
    api.put(`/api/v1/uoms/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/uoms/${id}`),
};

// ---------- Service master ----------
export interface ServiceDto {
  id: string; code: string; name: string; sacCode?: string;
  defaultRate: number; gstRate: number; glAccountId?: string; isActive: boolean;
}

export const servicesApi = {
  list: (params: { search?: string; isActive?: boolean; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<ServiceDto>>("/api/v1/services", { params }).then(r => r.data),
  get: (id: string) => api.get<ServiceDto>(`/api/v1/services/${id}`).then(r => r.data),
  create: (payload: Partial<ServiceDto>) =>
    api.post<{ id: string }>("/api/v1/services", payload).then(r => r.data),
  update: (id: string, payload: Partial<ServiceDto>) =>
    api.put(`/api/v1/services/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/services/${id}`),
};

// ---------- Item Category + Form masters ----------
// itemType scopes a row to one item type; null/undefined = global (shown for every type).
export interface NamedMasterDto { id: string; name: string; itemType?: string | null }
export interface NamedMasterListParams { search?: string; itemType?: string; page?: number; pageSize?: number }
export interface NamedMasterPayload { name: string; itemType?: string | null }

export const itemCategoriesApi = {
  list: (params: NamedMasterListParams = {}) =>
    api.get<PaginatedList<NamedMasterDto>>("/api/v1/item-categories", { params }).then(r => r.data),
  create: (payload: NamedMasterPayload) =>
    api.post<{ id: string }>("/api/v1/item-categories", payload).then(r => r.data),
  update: (id: string, payload: NamedMasterPayload) =>
    api.put(`/api/v1/item-categories/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/item-categories/${id}`),
};

export const itemFormsApi = {
  list: (params: NamedMasterListParams = {}) =>
    api.get<PaginatedList<NamedMasterDto>>("/api/v1/item-forms", { params }).then(r => r.data),
  create: (payload: NamedMasterPayload) =>
    api.post<{ id: string }>("/api/v1/item-forms", payload).then(r => r.data),
  update: (id: string, payload: NamedMasterPayload) =>
    api.put(`/api/v1/item-forms/${id}`, { id, ...payload }),
  delete: (id: string) => api.delete(`/api/v1/item-forms/${id}`),
};

// ---------- PO state transitions ----------
export const purchaseOrderStateApi = {
  submit:  (id: string, comment?: string) => api.post(`/api/v1/purchase-orders/${id}/submit`, { comment }),
  approve: (id: string, comment?: string) => api.post(`/api/v1/purchase-orders/${id}/approve`, { comment }),
  reject:  (id: string, reason: string)  => api.post(`/api/v1/purchase-orders/${id}/reject`,  { reason }),
  cancel:  (id: string, reason: string)  => api.post(`/api/v1/purchase-orders/${id}/cancel`,  { reason }),
};

// ---------- PO / SI detail shapes (loose - backend DTOs are flat records) ----------
export interface PurchaseOrderLineDetailDto {
  id: string; itemId: string; itemName: string; sku: string;
  qty: number; rate: number; taxAmount: number; receivedQty: number;
  lineTotal: number; pendingQty: number;
  poLineSource?: string;
}
export interface PurchaseOrderDetailDto {
  id: string; documentNo: string; documentDate: string; status: string;
  vendorId: string; vendorName: string;
  warehouseId: string; warehouseName?: string;
  expectedDeliveryDate?: string; vendorRef?: string; notes?: string;
  subTotal?: number; taxAmount?: number; totalAmount: number;
  lines: PurchaseOrderLineDetailDto[];
}

export interface SalesInvoiceLineDetailDto {
  id: string; lineNo: number; lineType: LineType;
  itemId?: string; itemName?: string; sku?: string;
  serviceId?: string; serviceName?: string;
  description?: string; prescriptionRef?: string;
  substitutedFromItemId?: string; substitutedFromItemName?: string;
  substituteReasonCode?: string;
  qty: number; rate: number; taxAmount: number; discountAmount: number;
  lineTotal: number;
}
export interface SalesInvoicePaymentDto {
  id: string; mode: PaymentLineRequest["mode"];
  amountReceived: number; reference?: string; paidAt: string;
}
export interface SalesInvoiceDetailDto {
  id: string; documentNo: string; documentDate: string; status: string; billType: string;
  customerId?: string; customerName?: string; customerPhone?: string;
  warehouseId: string;
  subTotal: number; taxAmount: number; discountAmount: number; netAmount: number;
  journalEntryId?: string;
  notes?: string;
  lines: SalesInvoiceLineDetailDto[];
  payments: SalesInvoicePaymentDto[];
}

// ---------- Stock Adjustments ----------
export interface StockAdjustmentLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string; expiryDate?: string;
  qtyDelta: number; lineReason?: string;
}
export interface StockAdjustmentListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  warehouseId: string; warehouseName: string; reason: string;
  totalAmount: number; lineCount: number;
}
export interface StockAdjustmentDetailDto extends StockAdjustmentListDto {
  notes?: string; lines: StockAdjustmentLineDto[];
}
export interface CreateStockAdjustmentLineRequest {
  itemId: string; batchId: string; qtyDelta: number; reason?: string;
}
export interface CreateStockAdjustmentRequest {
  warehouseId: string; documentDate: string; reason: string; notes?: string;
  lines: CreateStockAdjustmentLineRequest[];
}

export const stockAdjustmentsApi = {
  list: (params: { status?: string; warehouseId?: string; fromDate?: string; toDate?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<StockAdjustmentListDto>>("/api/v1/stock-adjustments", { params }).then(r => r.data),
  get: (id: string) => api.get<StockAdjustmentDetailDto>(`/api/v1/stock-adjustments/${id}`).then(r => r.data),
  create: (payload: CreateStockAdjustmentRequest) =>
    api.post<{ id: string }>("/api/v1/stock-adjustments", payload).then(r => r.data),
  post: (id: string) => api.post(`/api/v1/stock-adjustments/${id}/post`),
};

// ---------- S4 — Inventory Ops (Write-off / Internal Transfer / Stock Take / Near Expiry) ----------
export type WriteOffReason = "Expired" | "Damaged" | "Lost" | "QcReject";

export interface StockWriteOffListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  warehouseId: string; warehouseName: string; reason?: string;
  totalQty: number; lineCount: number;
}
export interface StockWriteOffLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string; expiryDate?: string;
  qty: number; reasonCode: WriteOffReason; lineNotes?: string;
}
export interface StockWriteOffDetailDto extends StockWriteOffListDto {
  notes?: string; rowVersion: string; lines: StockWriteOffLineDto[];
}
export interface CreateStockWriteOffLineRequest {
  itemId: string; batchId: string; qty: number;
  reasonCode: WriteOffReason; lineNotes?: string;
}
export interface CreateStockWriteOffRequest {
  warehouseId: string; documentDate: string;
  reason?: string; notes?: string; lines: CreateStockWriteOffLineRequest[];
}
export const stockWriteOffsApi = {
  list: (params: { status?: string; warehouseId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<StockWriteOffListDto>>("/api/v1/stock/write-offs", { params }).then(r => r.data),
  get: (id: string) => api.get<StockWriteOffDetailDto>(`/api/v1/stock/write-offs/${id}`).then(r => r.data),
  create: (payload: CreateStockWriteOffRequest) =>
    api.post<{ id: string }>("/api/v1/stock/write-offs", payload).then(r => r.data),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/stock/write-offs/${id}/post`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

export interface InternalTransferListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  sourceWarehouseId: string; sourceWarehouseName: string;
  destWarehouseId: string; destWarehouseName: string;
  lineCount: number; totalQty: number; inTransitQty: number;
}
export interface InternalTransferLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string;
  qty: number; receivedQty: number; inTransitQty: number;
}
export interface InternalTransferDetailDto extends InternalTransferListDto {
  reason?: string; notes?: string; rowVersion: string; lines: InternalTransferLineDto[];
}
export interface CreateInternalTransferLineRequest { itemId: string; batchId: string; qty: number }
export interface CreateInternalTransferRequest {
  sourceWarehouseId: string; destWarehouseId: string;
  documentDate: string; reason?: string; notes?: string;
  lines: CreateInternalTransferLineRequest[];
}
export interface ReceiveInternalTransferRequest {
  lines: { lineId: string; receivedQty: number }[];
}
export const internalTransfersApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<InternalTransferListDto>>("/api/v1/internal-transfers", { params }).then(r => r.data),
  get: (id: string) => api.get<InternalTransferDetailDto>(`/api/v1/internal-transfers/${id}`).then(r => r.data),
  create: (payload: CreateInternalTransferRequest) =>
    api.post<{ id: string }>("/api/v1/internal-transfers", payload).then(r => r.data),
  dispatch: (id: string, rowVersion: string) =>
    api.post(`/api/v1/internal-transfers/${id}/dispatch`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
  receive: (id: string, payload: ReceiveInternalTransferRequest, rowVersion: string) =>
    api.post(`/api/v1/internal-transfers/${id}/receive`, payload, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

export interface StockTakeListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  warehouseId: string; warehouseName: string;
  lineCount: number; totalVariance: number;
}
export interface StockTakeLineDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string;
  systemQty: number; countedQty: number; varianceQty: number;
  lineNotes?: string;
}
export interface StockTakeDetailDto extends StockTakeListDto {
  notes?: string; rowVersion: string; lines: StockTakeLineDto[];
}
export interface CreateStockTakeRequest {
  warehouseToCountId: string; documentDate: string; notes?: string;
  batchIds?: string[];
}
export const stockTakesApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<StockTakeListDto>>("/api/v1/stock-takes", { params }).then(r => r.data),
  get: (id: string) => api.get<StockTakeDetailDto>(`/api/v1/stock-takes/${id}`).then(r => r.data),
  create: (payload: CreateStockTakeRequest) =>
    api.post<{ id: string }>("/api/v1/stock-takes", payload).then(r => r.data),
  updateCounts: (id: string, lines: { lineId: string; countedQty: number; lineNotes?: string }[]) =>
    api.put(`/api/v1/stock-takes/${id}/counts`, { lines }),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/stock-takes/${id}/post`, null, {
      headers: { "If-Match": `"${rowVersion}"` },
    }),
};

export interface NearExpiryRow {
  batchId: string; itemId: string; itemSku: string; itemName: string;
  form?: string; strength?: string;
  batchNo: string; expiryDate: string; daysToExpiry: number;
  warehouseId: string; warehouseName: string;
  onHandQty: number; costPrice: number; estimatedExposure: number;
}
export const nearExpiryApi = {
  list: (windowDays: number = 30, warehouseId?: string) =>
    api.get<NearExpiryRow[]>("/api/v1/inventory/near-expiry", {
      params: { windowDays, warehouseId },
    }).then(r => r.data),
};

// ---------- S4 — Reorder suggestions + bulk PO ----------
export interface ReorderSuggestionRow {
  itemId: string; sku: string; name: string; genericName?: string;
  form?: string; packaging?: string; strength?: string;
  availableQty: number; reorderLevel: number; suggestedQty: number;
  lastPurchaseRate?: number; lastVendorId?: string; lastVendorName?: string;
  isOutOfStock: boolean;
}
export type ReorderSupplierStrategy = "LastSupplier" | "BestRate6Mo" | "SpecificVendor";
export interface CreatePOsFromReorderRequest {
  warehouseId: string; itemIds: string[];
  strategy: ReorderSupplierStrategy; vendorIdOverride?: string;
}
export interface CreatedPoSummary {
  purchaseOrderId: string; vendorId: string; vendorName: string;
  lineCount: number; totalQty: number; estimatedTotal: number;
}
export interface CreatePOsFromReorderResponse {
  createdPurchaseOrders: CreatedPoSummary[];
  unresolvedItemIds: string[];
}
export const reorderApi = {
  suggestions: (warehouseId: string) =>
    api.get<ReorderSuggestionRow[]>("/api/v1/inventory/reorder-suggestions", {
      params: { warehouseId },
    }).then(r => r.data),
  createPOs: (payload: CreatePOsFromReorderRequest) =>
    api.post<CreatePOsFromReorderResponse>("/api/v1/purchase-orders/from-reorder-suggestions", payload)
       .then(r => r.data),
};

// ---------- S11 — Hospital Ward / OT / CSSD ----------
export interface WardIssueListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  uhid: string; wardCode?: string; isEmergency: boolean;
  wardWarehouseId: string; lineCount: number;
}
export interface WardIssueLineReadDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; batchNo?: string; qty: number; lineNotes?: string;
}
export interface WardIssueDetailDto {
  id: string; documentNo: string; documentDate: string; status: string;
  uhid: string; admissionId?: string; wardCode?: string;
  wardWarehouseId: string; isEmergency: boolean; reason?: string;
  rowVersion: string; lines: WardIssueLineReadDto[];
}
export interface CreateWardIssueLineDto { itemId: string; batchId: string; qty: number; lineNotes?: string }
export interface CreateWardIssueRequest {
  uhid: string; admissionId?: string; wardCode?: string; wardWarehouseId: string;
  isEmergency: boolean; reason?: string; lines: CreateWardIssueLineDto[];
}
export const wardIssuesApi = {
  list: (params: { status?: string; uhid?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<WardIssueListDto>>("/api/v1/ward-issues", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<WardIssueDetailDto>(`/api/v1/ward-issues/${id}`).then(r => r.data),
  create: (payload: CreateWardIssueRequest) =>
    api.post<{ id: string }>("/api/v1/ward-issues", payload).then(r => r.data),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/ward-issues/${id}/post`, undefined, { headers: { "If-Match": rowVersion } }),
};

export interface OtConsumptionListDto {
  id: string; documentNo: string; documentDate: string; status: string;
  uhid: string; procedureCode: string; surgeon?: string;
  lineCount: number; implantCount: number;
}
export interface OtConsumptionLineReadDto {
  id: string; itemId: string; itemName: string; sku: string;
  batchId: string; qty: number; isImplant: boolean;
}
export interface ImplantReadDto { id: string; itemId: string; udi: string; implantedAt: string }
export interface OtConsumptionDetailDto {
  id: string; documentNo: string; documentDate: string; status: string;
  uhid: string; admissionId?: string; procedureCode: string; surgeon?: string;
  procedureAt?: string; otWarehouseId: string; surgicalKitId?: string;
  rowVersion: string; lines: OtConsumptionLineReadDto[]; implants: ImplantReadDto[];
}
export interface CreateOtConsumptionLineDto { itemId: string; batchId: string; qty: number; isImplant: boolean }
export interface CreateOtImplantDto { itemId: string; batchId: string; udi: string }
export interface CreateOtConsumptionRequest {
  uhid: string; admissionId?: string; procedureCode: string; otWarehouseId: string;
  surgicalKitId?: string; surgeon?: string; procedureAt?: string;
  lines: CreateOtConsumptionLineDto[]; implants: CreateOtImplantDto[];
}
export const otConsumptionsApi = {
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<OtConsumptionListDto>>("/api/v1/ot-consumptions", { params }).then(r => r.data),
  get: (id: string) =>
    api.get<OtConsumptionDetailDto>(`/api/v1/ot-consumptions/${id}`).then(r => r.data),
  create: (payload: CreateOtConsumptionRequest) =>
    api.post<{ id: string }>("/api/v1/ot-consumptions", payload).then(r => r.data),
  post: (id: string, rowVersion: string) =>
    api.post(`/api/v1/ot-consumptions/${id}/post`, undefined, { headers: { "If-Match": rowVersion } }),
};

export type CssdCycleStatus = "InUse" | "Sterilizing" | "Sterile" | "Quarantined";
export interface CssdTrayDto {
  id: string; code: string; name: string; contents?: string;
  currentStatus: CssdCycleStatus; lastCycleAt?: string; isActive: boolean; cycleCount: number;
}
export interface CreateCssdTrayRequest { code: string; name: string; contents?: string }
export interface AddCycleRequest { status: CssdCycleStatus; notes?: string }
export const cssdTraysApi = {
  list: (params: { page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<CssdTrayDto>>("/api/v1/cssd-trays", { params }).then(r => r.data),
  create: (payload: CreateCssdTrayRequest) =>
    api.post<{ id: string }>("/api/v1/cssd-trays", payload).then(r => r.data),
  addCycle: (id: string, payload: AddCycleRequest) =>
    api.post(`/api/v1/cssd-trays/${id}/cycle`, payload),
};

// ---------- S12 — HealNTrack integration ----------
export type HealNTrackDirection = "Inbound" | "Outbound";
export type HealNTrackEventStatus = "Pending" | "Processed" | "Failed" | "Dead";
export interface HealNTrackEventRow {
  id: string;
  direction: HealNTrackDirection;
  eventType: string;
  uhid: string;
  admissionId?: string;
  status: HealNTrackEventStatus;
  attemptCount: number;
  at: string;
  lastError?: string;
}
export interface HealNTrackOpenChargeDto {
  salesInvoiceId: string; invoiceNo: string; invoiceDate: string;
  total: number; paid: number; outstanding: number;
}
export const healnTrackApi = {
  list: (params: {
    direction?: HealNTrackDirection; status?: HealNTrackEventStatus; uhid?: string;
    page?: number; pageSize?: number;
  } = {}) =>
    api.get<PaginatedList<HealNTrackEventRow>>("/api/v1/integrations/healntrack/v1/events",
      { params }).then(r => r.data),
  replay: (id: string) =>
    api.post(`/api/v1/integrations/healntrack/v1/events/${id}/replay`),
  openCharges: (uhid: string) =>
    api.get<HealNTrackOpenChargeDto[]>(
      `/api/v1/integrations/healntrack/v1/patients/${encodeURIComponent(uhid)}/open-charges`)
      .then(r => r.data),
};

// ---------- S13 — GST e-invoice + e-way bill ----------
export type EInvoiceStatus = "Pending" | "Generated" | "Failed" | "Cancelled" | "NotRequired";
export type EWayBillStatus = "Pending" | "Generated" | "Failed" | "Expired" | "NotRequired";
export interface EInvoiceRow {
  id: string; salesInvoiceId: string; invoiceNo: string;
  status: EInvoiceStatus; irn?: string; ackNo?: string; ackDate?: string;
  attemptCount: number; lastAttemptAt?: string; manuallyOverridden: boolean; lastError?: string;
}
export interface EWayBillRow {
  id: string; shipmentId: string; transferDocumentNo: string;
  status: EWayBillStatus; ewbNo?: string; generatedAt?: string; validUntil?: string;
  attemptCount: number; lastAttemptAt?: string; manuallyEntered: boolean; lastError?: string;
}
export const gstApi = {
  listEInvoices: (params: { status?: EInvoiceStatus; q?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<EInvoiceRow>>("/api/v1/integrations/gst/einvoices", { params }).then(r => r.data),
  retryEInvoice: (id: string) =>
    api.post(`/api/v1/integrations/gst/einvoices/${id}/retry`),
  listEWayBills: (params: { status?: EWayBillStatus; q?: string; page?: number; pageSize?: number } = {}) =>
    api.get<PaginatedList<EWayBillRow>>("/api/v1/integrations/gst/ewaybills", { params }).then(r => r.data),
  retryEWayBill: (id: string) =>
    api.post(`/api/v1/integrations/gst/ewaybills/${id}/retry`),
};

// ---------- S14 — VaidSetu, predictive reorder, narcotic register ----------
export interface VaidSetuInteraction {
  severity: "info" | "moderate" | "severe";
  pair: string[]; message: string; action: string;
}
export interface VaidSetuAllergy { substance: string; severity: string; message: string }
export interface VaidSetuResult {
  interactions: VaidSetuInteraction[]; allergies: VaidSetuAllergy[]; blocked: boolean;
}
export const vaidSetuApi = {
  check: (itemIds: string[], patientRef?: string) =>
    api.post<VaidSetuResult>("/api/v1/integrations/vaidsetu/check", { itemIds, patientRef })
      .then(r => r.data),
};

export interface PredictiveReorderRow {
  itemId: string; sku: string; name: string; genericName?: string;
  availableQty: number; avgDailyConsumption: number; leadTimeDays: number;
  safetyStock: number; predictedReorderPoint: number; daysOfCover?: number;
  suggestedQty: number; shouldReorder: boolean;
}
export const predictiveReorderApi = {
  list: (warehouseId: string, windowDays = 90, onlyBelowRop = true) =>
    api.get<PredictiveReorderRow[]>("/api/v1/inventory/predictive-reorder", {
      params: { warehouseId, windowDays, onlyBelowRop },
    }).then(r => r.data),
};

export interface NarcoticRegisterRow {
  at: string; itemId: string; sku: string; name: string; scheduleCategory?: string;
  batchNo: string; movement: string; qtyIn: number; qtyOut: number;
  runningBalance: number; referenceType?: string; referenceId?: string;
}
export const narcoticApi = {
  list: (params: { itemId?: string; from?: string; to?: string } = {}) =>
    api.get<NarcoticRegisterRow[]>("/api/v1/inventory/narcotic-register", { params }).then(r => r.data),
};
